import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AdressePhysiqueBancaire } from 'src/app/api/model/adresse-physique-bancaire.model';
import { CompteSCGP } from 'src/app/api/model/compte-scgp.model';
import { ParticulierBancaire } from 'src/app/api/model/particulier-bancaire.model';
import { TelephoneBancaire } from 'src/app/api/model/telephone-bancaire.model';
import { FormulaireCoordonneesBancaires } from 'src/app/models/formulaire/formulaire-coordonnees-bancaires.model';
import { TelephoneTypeEnum } from 'src/app/models/telephone-type.enum';
import { TelephoneTypeLieuEnum } from 'src/app/models/telephone-typelieu.enum';
import { ChangementCoordonneeBancaireAccesDService } from 'src/app/services/changement-coordonnee-bancaire-accesd/changement-coordonnee-bancaire-acces-d.service';
import { FormulaireBancaireService } from 'src/app/services/formulaire-bancaire/formulaire-bancaire.service';
import { GabaritService } from 'src/app/services/gabarit/gabarit.service';
import { GestionErreurService } from 'src/app/services/gestion-erreur/gestion-erreur.service';
import { GlobalStoreService } from 'src/app/services/global-store/global-store.service';
import { GestionTitreService } from 'src/app/services/navigation/gestion-titre.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { RequeteCoordonneesBancaireService } from 'src/app/services/requete-coordonnees-bancaire/requete-coordonnees-bancaire.service';
import { CoordonneesUtil } from 'src/app/util/coordonnees-util';
import { DateUtil } from 'src/app/util/date-util';
import { CybermetrieCoordonneesService } from '../../services/cybermetrie/cybermetrie.service';
import { CybermetrieEnum } from '../../models/cybermetrie/cybermetrie.enum';
import { AccessibiliteService } from '../../services/accessibilite.service';

@Component({
  selector: 'app-page-validation',
  templateUrl: './page-validation.component.html',
  styleUrls: ['./page-validation.component.scss'],
  standalone: false,
})
export class PageValidationComponent implements OnInit, AfterViewInit {
  isAppMobile: boolean;
  form: FormGroup<FormulaireCoordonneesBancaires>;
  codeErreur: string = '';
  adresseSaisie: AdressePhysiqueBancaire;
  dateChangement: string;
  dateUtil = new DateUtil();
  coordonneesMembre?: ParticulierBancaire;
  comptesSelectionnes: Array<CompteSCGP> = [];

  constructor(
    private readonly gabaritService: GabaritService,
    private readonly gestionTitreService: GestionTitreService,
    private readonly navigationService: NavigationService,
    private readonly globalStoreService: GlobalStoreService,
    private readonly formulaireBancaireService: FormulaireBancaireService,
    private readonly gestionErreurService: GestionErreurService,
    private readonly requeteCoordonneesBancaireService: RequeteCoordonneesBancaireService,
    private readonly coordonneesBancaireService: ChangementCoordonneeBancaireAccesDService,
    private readonly cybermetrieCoordonneesService: CybermetrieCoordonneesService,
    private readonly accessibiliteService: AccessibiliteService
  ) {}

  ngOnInit(): void {
    this.getCoordonneesMembre();
    this.isAppMobile = this.gabaritService.isAppMobile();
    this.gestionTitreService.addTitreOnglet('caad.titreOnglet.page-validation');
    this.form = this.formulaireBancaireService.getForm();
    this.initAdresseSaisie();
    this.dateChangement = this.form.value.dateChangement ?? '';
    this.comptesSelectionnes = this.comptes;
  }

  ngAfterViewInit() {
    this.cybermetrieCoordonneesService.transactionCoordonneesBancaire(CybermetrieEnum.ACTION_ETAPE);
    this.accessibiliteService.setFocus('#titreValidation');
  }

  get courriel(): string {
    return this.form?.value.courriel ?? '';
  }

  private getTelephone(
    type: 'telephonePrincipal' | 'telephoneTravail' | 'telephoneSecondaire',
    typeLieu: string
  ): TelephoneBancaire | undefined {
    const telephone = this.form.value[type];
    return telephone
      ? ({
          typeAppareil: telephone.typeTelephone,
          numeroTelephone: telephone.telephone,
          numeroPoste: telephone.poste,
          typeLieu,
        } as TelephoneBancaire)
      : undefined;
  }

  get telephonePrincipal(): TelephoneBancaire | undefined {
    return this.getTelephone('telephonePrincipal', TelephoneTypeLieuEnum.PRINCIPALE);
  }

  get telephoneTravail(): TelephoneBancaire | undefined {
    return this.getTelephone('telephoneTravail', TelephoneTypeLieuEnum.TRAVAIL);
  }

  get telephoneSecondaire(): TelephoneBancaire | undefined {
    return this.getTelephone('telephoneSecondaire', TelephoneTypeLieuEnum.SECONDAIRE);
  }

  typeAppareilTelephone(telephone: TelephoneBancaire | undefined): string {
    if (!telephone) {
      return '';
    }
    switch (telephone.typeAppareil) {
      case TelephoneTypeEnum.TELEPHONE:
        return 'caad.modifier-coordonnees.telephones.typeFixe';
      default:
        return 'caad.modifier-coordonnees.telephones.typeCellulaire';
    }
  }

  private initAdresseSaisie(): void {
    if (this.form.value.utiliserMemeAdresse) {
      this.adresseSaisie = this.coordonneesMembre?.adresseCourante ?? null;
    } else {
      this.adresseSaisie = this.getAdresseSaisie();
    }
  }

  private getAdresseSaisie(): AdressePhysiqueBancaire {
    const { numeroCivique, nomRue } = CoordonneesUtil.separerNumeroCiviqueEtNomRue(
      this.form.value.numeroCiviqueNomRue ?? ''
    );

    return {
      numeroCivique: numeroCivique ?? '',
      nomRue: nomRue ?? '',
      numeroUnite: this.form.value.appartement ?? '',
      nomMunicipalite: this.form.value.ville ?? '',
      codeSubdivisionPays: this.form.value.province ?? '',
      codePostal: this.form.value.codePostal ?? '',
      codePays: this.form.value.codePays ?? '',
      numeroCiviqueNomRue: this.form.value.numeroCiviqueNomRue ?? '',
    } as AdressePhysiqueBancaire;
  }

  get comptes(): Array<CompteSCGP> {
    return this.coordonneesMembre?.comptesSCGP ?? [];
  }

  private getCoordonneesMembre(): void {
    this.globalStoreService.getCoordonneesMembre$().subscribe({
      next: (data) => {
        this.coordonneesMembre = data;
      },
    });
  }

  onComptesSelectionnes(selection: Array<CompteSCGP>) {
    this.comptesSelectionnes = selection;
  }

  naviguerVersModifier(): void {
    this.navigationService.naviguerVersModifierCoordonnees();
    this.cybermetrieCoordonneesService.clickModifierCoordonneesBancaire(CybermetrieEnum.ACTION_ETAPE);
  }

  naviguerVersConfirmation(): void {
    this.cybermetrieCoordonneesService.clickCoordonneesBancaire(CybermetrieEnum.LABEL_BANCAIRE_CONFIRMER);
    this.coordonneesBancaireService
      .modifierCoordonnees$(
        this.requeteCoordonneesBancaireService.construireRequeteModifierCoordonnees(this.comptesSelectionnes)
      )
      .subscribe({
        next: () => {
          this.navigationService.naviguerVersCoordonneesConfirmation();
        },
        error: (err: HttpErrorResponse) => {
          this.codeErreur = this.gestionErreurService.getCodeErreur(err);
          this.navigationService.naviguerVersPageErreurCoordonnees(
            err.status.toString(),
            this.codeErreur,
            this.gestionErreurService.getMessageErreur(err)
          );
        },
      });
  }

  get afficherDateChangement(): boolean {
    //Nous n'utilisons pas parse ici, car le parsing avec le format sans heure est toujours interprétée en UTC
    //ce qui pourrait potentiellement causer un décalage d’un jour selon le fuseau horaire du membre.
    if (!this.dateChangement) return false;
    const aujourdHui = new Date();
    const [year, month, day] = this.dateChangement.split('-').map(Number);
    const datePostdatee = new Date(year, month - 1, day);

    return datePostdatee > aujourdHui;
  }

  get libelleAdresse(): string {
    return this.dateUtil.estMemeJour(this.dateChangement)
      ? 'caad.page-validation.coordonnees.adresse'
      : 'caad.page-validation.coordonnees.nouvelleAdresse';
  }

  annuler(): void {
    this.globalStoreService.saveDeclancherModale(CybermetrieEnum.ACTION_ETAPE);
    this.globalStoreService.saveEtatAfficherModaleAnnuler(true);
  }
}
